import React, { useState, useEffect } from 'react';
import { Bell, BellRing } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { docketFollowService, WATCHLIST_LIMIT_CODE } from '../lib/docketFollowService';
import { useAuth } from '../contexts/AuthContext';
import { useRequireAuth, consumePendingAuthIntent } from '../hooks/useRequireAuth';
import { track } from '../utils/analytics';

interface FollowButtonProps {
  docketNumber: string;
  className?: string;
  tone?: 'default' | 'dark';
  /** Where this button lives, for funnel attribution (e.g. 'digest', 'proceeding'). */
  source?: string;
  onToggle?: (isFollowing: boolean) => void;
}

export const FollowButton: React.FC<FollowButtonProps> = ({
  docketNumber,
  className = '',
  tone = 'default',
  source,
  onToggle,
}) => {
  const { user } = useAuth();
  const requireAuth = useRequireAuth();
  const navigate = useNavigate();
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    let cancelled = false;
    docketFollowService.isFollowing(docketNumber).then(status => {
      if (!cancelled) setIsFollowing(status);
    });
    return () => { cancelled = true; };
  }, [docketNumber, user]);

  const performToggle = async () => {
    try {
      setLoading(true);
      const newStatus = await docketFollowService.toggleFollow(docketNumber);
      setIsFollowing(newStatus);
      if (newStatus) track('docket_follow', { docket_number: docketNumber, source: source || 'unknown' });
      onToggle?.(newStatus);
    } catch (error) {
      if ((error as { code?: string })?.code === WATCHLIST_LIMIT_CODE) {
        track('watchlist_limit_hit', { docket_number: docketNumber, source: source || 'unknown' });
        setShowUpgrade(true);
      } else {
        console.error('Error toggling follow:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () =>
    requireAuth(performToggle, {
      intentLabel: 'follow this docket',
      intentKey: `follow_docket:${docketNumber}`,
    });

  // Resume a follow the user requested while signed out. After the magic-link
  // sign-in redirects back to this page, AuthContext fires 'cg:auth-resume';
  // we claim only our own intent (consume is predicate-scoped so SummariesSection
  // doesn't swallow it). Follow only if not already following, so we never
  // accidentally toggle back off.
  useEffect(() => {
    const onResume = async () => {
      const pending = consumePendingAuthIntent(
        (i) => i.intentKey === `follow_docket:${docketNumber}`
      );
      if (!pending) return;
      try {
        setLoading(true);
        const already = await docketFollowService.isFollowing(docketNumber);
        const status = already ? true : await docketFollowService.toggleFollow(docketNumber);
        setIsFollowing(status);
        if (status && !already) track('docket_follow', { docket_number: docketNumber, source: source || 'auth_resume' });
        onToggle?.(status);
      } catch (error) {
        if ((error as { code?: string })?.code === WATCHLIST_LIMIT_CODE) {
          setShowUpgrade(true);
        } else {
          console.error('Error resuming follow after sign-in:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    window.addEventListener('cg:auth-resume', onResume);
    return () => window.removeEventListener('cg:auth-resume', onResume);
  }, [docketNumber, onToggle, source]);

  const surfaceClass =
    tone === 'dark'
      ? isFollowing
        ? 'bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/35 text-emerald-200 hover:text-emerald-100'
        : 'bg-white/[0.08] hover:bg-white/[0.13] border border-white/15 text-white/80 hover:text-white'
      : isFollowing
        ? 'bg-emerald-100 hover:bg-emerald-200 border border-emerald-300 text-emerald-700 hover:text-emerald-800'
        : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 hover:text-gray-700';

  return (
    <>
      <button
        onClick={handleToggle}
        disabled={loading}
        className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${surfaceClass} ${className}`}
        title={isFollowing ? 'Unfollow this docket' : 'Follow this docket for alerts'}
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : isFollowing ? (
          <BellRing className="w-4 h-4" />
        ) : (
          <Bell className="w-4 h-4" />
        )}
        <span className="hidden sm:inline">
          {isFollowing ? 'Following' : 'Follow'}
        </span>
      </button>

      {showUpgrade && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setShowUpgrade(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Watchlist limit reached"
            className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-7 text-center shadow-xl"
          >
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
              <BellRing className="h-6 w-6 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">You&apos;re following 5 dockets</h3>
            <p className="mt-2 text-sm text-gray-500">
              Free accounts can follow up to 5 dockets. Upgrade to Pro for unlimited watched
              dockets, alerts, and AI digests.
            </p>
            <button
              type="button"
              onClick={() => { setShowUpgrade(false); navigate('/pricing'); }}
              className="mt-5 w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800"
            >
              Upgrade to Pro
            </button>
            <button
              type="button"
              onClick={() => setShowUpgrade(false)}
              className="mt-2 w-full text-sm text-gray-400 transition-colors hover:text-gray-600"
            >
              Not now
            </button>
          </div>
        </div>
      )}
    </>
  );
};
